import React, { useMemo } from "react";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import { useSelector } from "react-redux";
import { useState } from "react";
import { getButtonCategory } from "@utils/product/productValidators";
import { useTheme } from "styled-components/native";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useAskSupplier } from "@apis/askSupplier";
import { Button } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import { AskSupplierScreenProps } from "./types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import AskSupplierForm from "./FormComponent";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { ProductCategory } from "@utils/constants";
import { useBulkCheckout } from "@apis/bulkCheckout";
import { getStockItemType } from "@scenes/searchResults/Utils";
import { DefaultTheme } from "styled-components/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const AskSupplierScreen = ({
  onClose,
  itemDetail,
  onSuccess,
  fromReviewPage,
}: AskSupplierScreenProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const askSupplier = useAskSupplier();
  const [countryCode, setCountyCode] = useState("");
  const [isCommentsValid, setIsCommentsValid] = useState(false);
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const { bottom } = useSafeAreaInsets();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema(t, isGuest || userInfo?.isIFrame())),
  });

  const {
    itemDetail: mItemDetail,
    stoneItemDetail: mStoneItemDetail,
    jcSelectedOption,
    personalization,
    reviewTotalPrice,
    matchingPairItemDetail,
  } = useJewelryConfiguratorState();

  const jewelryItemDetail = useMemo(
    () =>
      mItemDetail?.type !== ProductCategory.JEWELRY &&
      mItemDetail?.type !== ProductCategory.LABGROWN_JEWELRY
        ? mStoneItemDetail
        : mItemDetail,
    [mItemDetail?.type]
  );

  const stoneItemDetail = useMemo(
    () =>
      mStoneItemDetail?.type === ProductCategory.JEWELRY ||
      mStoneItemDetail?.type === ProductCategory.LABGROWN_JEWELRY
        ? mItemDetail
        : mStoneItemDetail,
    [mStoneItemDetail?.type]
  );

  const { trackButtonEvent } = useUserAnalytics() as any;
  const bulkCheckout = useBulkCheckout();
  const params = (data?: any) => {
    return {
      bulk_request: {
        additional_attributes: {
          jewelry_builder: {
            config: jcSelectedOption.current.option_value || {},
            personalization: personalization || {},
            selected_primary_jewelry_id: jewelryItemDetail.id,
            selected_jewelry_id: jewelryItemDetail.id,
            selected_stock_item_ids: [
              stoneItemDetail.id,
              ...(matchingPairItemDetail ? [matchingPairItemDetail.id] : []),
            ],
          },
        },
        comments: data.comments || "",
        offer_total_sales_price: reviewTotalPrice,
        price_mode: userInfo.getPriceMode(),
        request_type: `${jewelryItemDetail?.type}_ask`,
        secondary_request_type: "jewelry_builder",
        stock_item_request: [
          {
            offer_price_per_carat: jewelryItemDetail.price_per_carat ?? null,
            offer_total_sales_price:
              jewelryItemDetail.total_sales_price ?? null,
            stock_item_id: jewelryItemDetail.id,
            stock_item_type: getStockItemType(
              jewelryItemDetail?.type as ProductCategory
            ),
            config: jcSelectedOption.current.option_value || {},
            personalization: personalization || {},
          },
          {
            offer_discount_percent: stoneItemDetail.discount_percent ?? null,
            offer_price_per_carat: stoneItemDetail.price_per_carat ?? null,
            offer_total_sales_price: stoneItemDetail.total_sales_price ?? null,
            stock_item_id: stoneItemDetail.id,
            stock_item_type: getStockItemType(
              stoneItemDetail?.type as ProductCategory
            ),
          },
          ...(matchingPairItemDetail
            ? [
                {
                  offer_discount_percent:
                    matchingPairItemDetail.discount_percent ?? null,
                  offer_price_per_carat:
                    matchingPairItemDetail.price_per_carat ?? null,
                  offer_total_sales_price:
                    matchingPairItemDetail.total_sales_price ?? null,
                  stock_item_id: matchingPairItemDetail.id,
                  stock_item_type: getStockItemType(
                    matchingPairItemDetail?.type as ProductCategory
                  ),
                },
              ]
            : []),
        ],
        stock_item_type: jewelryItemDetail?.type?.toString(),
      },
    };
  };

  const placeRequest = (data?: any) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.ASK_SUPPLIER,
      getAnalyticsProductData(jewelryItemDetail)
    );
    bulkCheckout.mutate(params(data), {
      onSuccess() {
        onClose();
        onSuccess();
      },
    });
  };

  const handleEvent = (data: any) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.ASK_SUPPLIER,
      getAnalyticsProductData(itemDetail)
    );
    askSupplier.mutate(
      {
        ...((isGuest || userInfo?.isIFrame()) && {
          name: data.username,
          email: data.email,
          phone: data.phoneNumber,
          phone_number_country_code: countryCode ? `${countryCode}` : "+1",
        }),
        comments: data.comments,
        location_id: itemDetail?.locations?.[0]?.id,
        price_mode: userInfo.getUserPriceMode(),
        request_type: `${itemDetail?.type}_ask`,
        stock_item_id: itemDetail?.id,
        ...(itemDetail?.display_configuration_v2?.length > 0 &&
          itemDetail?.custom_design_configuration && {
            additional_attributes: {
              config: jcSelectedOption.current.option_value,
              personalization: personalization ?? {},
            },
          }),
      },
      {
        onSuccess() {
          onClose();
          onSuccess();
        },
      }
    );
  };
  return (
    <>
      <AskSupplierForm
        setIsCommentsValid={setIsCommentsValid}
        theme={theme}
        t={t}
        isGuest={isGuest || userInfo?.isIFrame()}
        control={control}
        countryCode={countryCode}
        setCountyCode={setCountyCode}
        askSupplier={fromReviewPage ? bulkCheckout : askSupplier}
        fromReviewPage={fromReviewPage}
      />
      <ButtonContainer theme={theme} bottom={bottom}>
        <ContainerOverlay>
          <Button
            type={ButtonType.primary}
            category={getButtonCategory(itemDetail?.type)}
            title={t("askSupplier.button.text")}
            onPress={
              fromReviewPage
                ? handleSubmit(placeRequest)
                : handleSubmit(handleEvent)
            }
            disabled={
              fromReviewPage
                ? bulkCheckout.isLoading
                : (isGuest || userInfo?.isIFrame()
                    ? !isValid
                    : !isCommentsValid) || askSupplier.isLoading
            }
            loading={
              fromReviewPage ? bulkCheckout.isLoading : askSupplier.isLoading
            }
          />
        </ContainerOverlay>
      </ButtonContainer>
    </>
  );
};

const ButtonContainer = styled.View<{
  itemType?: any;
  theme?: any;
  bottom?: number;
}>`
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? `${SpaceType.Double}px` : `${SpaceType.Big}px`};
  padding-bottom: ${({
    theme,
    bottom,
  }: {
    theme: DefaultTheme;
    bottom: number;
  }) =>
    theme.isLargeScreen
      ? `${SpaceType.ExtraSmall}px`
      : `${bottom > 0 ? bottom : SpaceType.ExtraLarge}px`};
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen
      ? `${SpaceType.ExtraSmall}px`
      : `${SpaceType.Double}px`};
  align-items: center;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default AskSupplierScreen;
